import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Eye, Film, Flame, TrendingUp, Twitch, Youtube, ExternalLink, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { LandingNav } from "@/components/landing/LandingNav";
import { Footer } from "@/components/Footer";
import { PublicSkeleton } from "@/components/Skeletons";
import { FitVideo, RankBadge } from "@/components/landing/ClipVisuals";
import { Reactions } from "@/components/Reactions";
import { FollowBox, CopyProfileLink } from "@/components/FollowBox";
import { FollowAccountButton } from "@/components/FollowAccountButton";
import { useAuth } from "@/context/AuthContext";
import { RefMonthBadge } from "@/components/referral/ReferralLeaderboard";

const LINK_META = { twitch: ["Twitch", "#9146ff", Twitch], kick: ["Kick", "#53fc18", ExternalLink], youtube: ["YouTube", "#ff3b3b", Youtube], tiktok: ["TikTok", "#ff0050", ExternalLink] };

export const ClipTile = ({ g, i, prefix }) => (
  <div className="cx-rise" style={{ animationDelay: `${Math.min(i, 8) * 60}ms` }} data-testid={`${prefix}-card-${i}`}>
    <Link to={`/c/${g.share_id}`} className="group block relative aspect-[9/16] rounded-3xl overflow-hidden bg-black border border-white/10 transition-transform duration-300 hover:-translate-y-1 hover:border-[#2f7bff]/50">
      <FitVideo sid={g.share_id} aspect={g.aspect} testid={`${prefix}-video-${i}`} />
      <div className="absolute top-3 left-3 right-3 flex flex-wrap gap-1.5 items-start">
        {g.badge && <RankBadge badge={g.badge} testid={`${prefix}-badge-${i}`} />}
        {g.game && <span className="rounded-full bg-black/60 backdrop-blur px-2.5 py-1 text-[10px] text-slate-200">{g.game}</span>}
      </div>
      <div className="absolute inset-x-0 bottom-0 p-4 pt-10 bg-gradient-to-t from-black/95 via-black/60 to-transparent">
        <p className="text-white text-sm font-semibold line-clamp-2">{g.title}</p>
        <p className="text-[11px] text-slate-300 mt-1 flex items-center gap-1 font-mono"><Eye className="w-3 h-3" />{g.views}</p>
      </div>
    </Link>
    <div className="mt-2.5 px-1 space-y-2">
      {g.handle ? <Link to={`/u/${g.handle}`} className="block text-xs text-[#3ad7ff] hover:text-white truncate" data-testid={`${prefix}-creator-${i}`}>@{g.handle}</Link>
        : <span className="block text-xs text-slate-400 truncate">{g.creator}</span>}
      <Reactions sid={g.share_id} initial={g.reactions} testid={`${prefix}-reactions-${i}`} />
    </div>
  </div>
);

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="cx-card rounded-2xl p-4" data-testid={testid}>
    <Icon className="w-4 h-4 text-[#3ad7ff]" />
    <div className="font-display text-2xl text-white mt-2">{value}</div>
    <div className="text-[11px] text-slate-500 uppercase tracking-wider mt-1">{label}</div>
  </div>
);

export default function CreatorProfile() {
  const { handle } = useParams();
  const { t } = useI18n();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/u/${handle}`).then((r) => setP(r.data)).catch(() => setP(false)); }, [handle]);
  if (p === null) return <PublicSkeleton testid="profile-loading" />;
  return (
    <div className="min-h-screen bg-[#04050a] text-white" data-testid="creator-profile-page">
      <LandingNav />
      <main className="max-w-6xl mx-auto px-5 sm:px-8 pt-32 pb-20">
        {p === false ? (
          <div className="text-center py-24" data-testid="profile-missing">
            <p className="text-slate-400">{t("pf.missing")}</p>
            <Link to="/vitrine" className="cx-btn rounded-full px-6 py-3 inline-block mt-6 text-sm font-semibold">{t("vt.see_all")}</Link>
          </div>
        ) : (<>
          <section className="cx-card cx-brackets rounded-3xl p-6 sm:p-10 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#a746ff]/20 blur-3xl" />
            <div className="relative flex flex-col sm:flex-row gap-6 sm:items-center">
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-[#2f7bff] to-[#a746ff] grid place-items-center font-display font-bold text-4xl shrink-0" data-testid="profile-avatar">
                {p.display_name.slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="font-display font-bold text-3xl sm:text-5xl cx-chrome truncate" data-testid="profile-name">{p.display_name}</h1>
                  {p.badge && <RankBadge badge={p.badge} testid="profile-badge" />}
                  {p.ref_month && <RefMonthBadge testid="profile-ref-month-badge" />}
                </div>
                <p className="text-[#3ad7ff] font-mono text-sm mt-1" data-testid="profile-handle">@{p.handle}{p.rank && <span className="text-slate-500"> · #{p.rank} {t("pf.this_month")}</span>}</p>
                {p.bio && <p className="text-slate-300 mt-3 max-w-2xl" data-testid="profile-bio">{p.bio}</p>}
                <div className="flex flex-wrap gap-2 mt-4">
                  {Object.entries(p.links).map(([k, url]) => {
                    const [label, color, Icon] = LINK_META[k];
                    return (
                      <a key={k} href={url} target="_blank" rel="noopener noreferrer nofollow" data-testid={`profile-link-${k}`}
                        className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm hover:border-white/30 transition-colors">
                        <Icon className="w-4 h-4" style={{ color }} />{label}
                      </a>
                    );
                  })}
                  {user && <FollowAccountButton handle={p.handle} name={p.display_name} onChange={(d) => setP((x) => ({ ...x, followers: (x.followers || 0) + d }))} />}
                  <CopyProfileLink handle={p.handle} />
                </div>
              </div>
            </div>
          </section>
          {!user && <div className="mt-6"><FollowBox handle={p.handle} name={p.display_name} /></div>}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-6">
            <Stat icon={Film} label={t("pf.clips")} value={p.stats.clips} testid="profile-stat-clips" />
            <Stat icon={Eye} label={t("pf.views")} value={p.stats.views} testid="profile-stat-views" />
            <Stat icon={TrendingUp} label={t("pf.month_views")} value={p.stats.month_views} testid="profile-stat-month" />
            <Stat icon={Flame} label={t("pf.reactions")} value={p.stats.reactions} testid="profile-stat-reactions" />
            <Stat icon={Users} label={t("pf.followers")} value={p.followers ?? 0} testid="profile-stat-followers" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 mt-10" data-testid="profile-clips">
            {p.clips.map((g, i) => <ClipTile key={g.share_id} g={g} i={i} prefix="profile" />)}
            {!p.clips.length && <p className="col-span-full text-slate-500" data-testid="profile-no-clips">{t("pf.no_clips")}</p>}
          </div>
        </>)}
      </main>
      <Footer />
    </div>
  );
}
